import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, TriangleAlert, XCircle } from "lucide-react";
import Button from "../components/Button/Button";
import { AppLoader } from "../components/AppLoader";
import { verifyNewEmail } from "../serverComm";

const VerifyNewEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("verifyButton");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const redirectTimeoutRef = useRef(null);

  const scheduleRedirect = useCallback(() => {
    if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    redirectTimeoutRef.current = setTimeout(() => navigate("/"), 5000);
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  const handleVerify = async (token) => {
    setLoading(true);
    verifyNewEmail(token)
      .then(() => {
        setStatus("success");
        scheduleRedirect();
      })
      .catch((error) => {
        setStatus("error");
        scheduleRedirect();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const token = params.get("token");

    if (!token) {
      setStatus("invalid");
      scheduleRedirect();
      return;
    }
    setCode(token);
    setStatus("verifyButton");
  }, [params, navigate, scheduleRedirect]);

  return (
    <div className="mt-40 flex flex-col items-center justify-center h-full">
      {status === "verifying" && (
        <div className="flex justify-center items-cente gap-2">
          <AppLoader color="black" />
          <span>Verifying your email...</span>
        </div>
      )}
      {status === "verifyButton" && (
        <div className="flex justify-center items-center gap-2">
          <Button onClick={() => handleVerify(code)}>
            {loading ? <AppLoader color="white" /> : "Click here"}
          </Button>{" "}
          to verify your email.
        </div>
      )}
      {status === "success" && (
        <div className="flex justify-center items-cente gap-2 bg-[#E0F0E4] p-4 text-sm rounded-xl mb-6 text-center text-[#377E36]">
          <CheckCircle className="w-6 h-6" />
          <span>
            Email verified successfully! You can login with your new email now.
            Redirecting to login...
          </span>
        </div>
      )}
      {status === "error" && (
        <div className="flex justify-center items-cente gap-2 bg-[#f0e0e0] p-4 text-sm rounded-xl mb-6 text-center text-[#7e3636]">
          <XCircle className="w-6 h-6" />
          <span>Verification failed or expired. Redirecting...</span>
        </div>
      )}
      {status === "invalid" && (
        <div className="flex justify-center items-cente gap-2 bg-[#f0ece0] p-4 text-sm rounded-xl mb-6 text-center text-[#7e6b36]">
          <TriangleAlert className="w-6 h-6" />
          <span>Invalid request. Redirecting...</span>
        </div>
      )}
    </div>
  );
};

export default VerifyNewEmail;
